import { useState } from 'react'
import { Button, Card as DsCard, Dashboard, DashColumn as DsDashColumn } from 'admination-react'
import { useTrackHover } from '@/core/hooks/useTrackHover'
import { useMultiDropZone } from '@/core/hooks/useDropZone'
import { pickFiles } from '@/core/lib/files'
import { useViewInput, type ViewInputState } from './useViewInput'
import type { BoardColumnConfig, BoardViewConfig, CardTone } from './viewConfig'

export type BoardHeaderAction<T = unknown> = (action: string, e: React.MouseEvent<HTMLElement>, rows: readonly T[]) => void
export type BoardCardAction<T> = (action: string, item: T, e: React.MouseEvent<HTMLElement>) => void

export type BoardHeaderFileAction = (
  action: string,
  files: readonly File[],
) => Promise<void> | void

type ActionableColumn<T> = BoardColumnConfig<T> & { actionObject: string; actionVerb: string; actionIcon: string }

function hasAction<T>(col: BoardColumnConfig<T>): col is ActionableColumn<T> {
  return col.actionObject !== undefined
}

function ActionCard<T>({
  col,
  onTileClick,
  isDragOver,
  isBusy,
  disabledHint,
}: {
  col: ActionableColumn<T>
  onTileClick: (e: React.MouseEvent<HTMLElement>) => void
  isDragOver: boolean
  isBusy: boolean

  disabledHint: string | null
}) {
  const { isHover, onMouseEnter, onMouseLeave } = useTrackHover()
  return (
    <article
      className={
        'card is-action' +
        (isHover ? ' is-hover' : '') +
        (isDragOver ? ' is-drag-over' : '') +
        (isBusy ? ' is-loading' : '') +
        (disabledHint ? ' is-disabled' : '')
      }
      aria-disabled={disabledHint ? true : undefined}
      tabIndex={isBusy || disabledHint ? undefined : -1}
      data-action={col.headerAction}
      data-droppable={col.headerDroppable ? '' : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={(e) => {
        if (isBusy || disabledHint) return
        onTileClick(e)
      }}
    >
      <span className="pill">

        <Button
          type="button"
          iconOnly
          aria-label={col.actionObject + ' ' + col.actionVerb}
          title={col.actionObject + ' ' + col.actionVerb}
        >
          <svg><use href={'#' + col.actionIcon} /></svg>
        </Button>
      </span>
      <p className="card-action-label">
        <span className="card-action-object">{col.actionObject}</span>{' '}
        <span className="card-action-verb">{disabledHint ?? col.actionVerb}</span>
      </p>

      {isBusy && <p className="card-loading-label">{col.headerBusyLabel}</p>}
    </article>
  )
}

const TONE_CLASS: Record<CardTone, string> = { warning: 'is-warning', danger: 'is-danger' }

function BoardCard<T>({
  item,
  col,
  active,
  rowKey,
  renderCard,
  onCardAction,
}: {
  item: T
  col: BoardColumnConfig<T>
  active: boolean
  rowKey: (item: T) => string
  renderCard: (item: T, col: BoardColumnConfig<T>) => React.ReactNode
  onCardAction?: BoardCardAction<T>
}) {
  const { isHover, onMouseEnter, onMouseLeave } = useTrackHover()
  const action = col.cardAction(item)
  const tone = col.cardTone?.(item)
  return (
    <DsCard
      isActive={active}
      className={[tone ? TONE_CLASS[tone] : '', !active && isHover ? 'is-hover' : ''].filter(Boolean).join(' ')}
      data-row-id={rowKey(item)}
      data-action={action}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onCardAction ? (e) => onCardAction(action, item, e) : undefined}
    >
      {renderCard(item, col)}
    </DsCard>
  )
}

function BoardColumn<T>({
  col,
  rows,
  loading,
  activeKey,
  rowKey,
  renderCard,
  onHeaderAction,
  onHeaderFile,
  onCardAction,
}: {
  col: BoardColumnConfig<T>
  rows: T[]
  loading: boolean
  activeKey?: string | null
  rowKey: (item: T) => string
  renderCard: (item: T, col: BoardColumnConfig<T>) => React.ReactNode
  onHeaderAction?: BoardHeaderAction<T>
  onHeaderFile?: BoardHeaderFileAction
  onCardAction?: BoardCardAction<T>
}) {
  const disabledHint = col.headerDisabledHint?.(rows) ?? null
  const actionable = hasAction(col) && rows.length > 0
  const droppable = actionable && Boolean(col.headerDroppable && col.headerAction && onHeaderFile)
  const [isBusy, setIsBusy] = useState(false)

  const runFiles = (files: readonly File[]) => {
    if (files.length === 0) return
    if (!actionable || !droppable || isBusy) return
    setIsBusy(true)
    void Promise.resolve(onHeaderFile!(col.headerAction!, files)).finally(() => setIsBusy(false))
  }
  const { isDragOver, handlers } = useMultiDropZone(runFiles)

  const onTileClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!col.headerAction) return
    if (col.headerAccept && droppable) {
      void pickFiles(col.headerAccept).then((files) => {
        if (files.length > 0) runFiles(files)
      })
      return
    }
    onHeaderAction?.(col.headerAction, e, rows)
  }

  return (
    <DsDashColumn
      title={col.title}
      count={loading ? undefined : rows.length}
      aria-busy={loading || undefined}
      data-clickable={col.clickable ? '' : undefined}
      data-group={col.id}
      {...(droppable ? handlers : {})}

      action={
        hasAction(col) && rows.length > 0 ? (
          <ActionCard
            col={col}
            onTileClick={onTileClick}
            isDragOver={droppable && isDragOver}
            isBusy={isBusy}
            disabledHint={disabledHint}
          />
        ) : undefined
      }
    >
      {rows.map((item) => (
        <BoardCard
          key={rowKey(item)}
          item={item}
          col={col}
          active={activeKey === rowKey(item)}
          rowKey={rowKey}
          renderCard={renderCard}
          onCardAction={onCardAction}
        />
      ))}
    </DsDashColumn>
  )
}

export function BoardView<T>({
  items,
  loading = false,
  config,
  viewState,
  activeKey,
  hiddenGroups,
  onHeaderAction,
  onHeaderFile,
  onCardAction,
}: {
  items: T[]

  loading?: boolean
  config: BoardViewConfig<T>
  viewState: ViewInputState
  activeKey?: string | null


  hiddenGroups?: string[]
  onHeaderAction?: BoardHeaderAction<T>

  onHeaderFile?: BoardHeaderFileAction
  onCardAction?: BoardCardAction<T>
}) {
  const { byGroup } = useViewInput(items, config, viewState)
  const shownColumns = config.columns.filter((c) => !hiddenGroups?.includes(c.id))

  return (
    <Dashboard data-dashboard-root>
      {shownColumns.map((col) => (
        <BoardColumn
          key={col.id}
          col={col}
          rows={byGroup?.get(col.id) ?? []}
          loading={loading}
          activeKey={activeKey}
          rowKey={config.rowKey}
          renderCard={config.renderCard}
          onHeaderAction={onHeaderAction}
          onHeaderFile={onHeaderFile}
          onCardAction={onCardAction}
        />
      ))}
    </Dashboard>
  )
}
